import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, Cloud } from "lucide-react";

/**
 * AutosaveIndicator — small pill showing autosave state next to a form.
 *
 * Props:
 *   status: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
 *   lastSavedAt: Date | null
 *
 * Iter-39.1: 'saved' state shows a live relative timestamp
 * ("just now" / "1m ago" / "23m ago" / "1h ago") that re-renders every
 * 30 seconds so candidates writing long-form text on a static page
 * keep getting subtle reassurance that their work is still safe.
 */
const AutosaveIndicator = ({ status, lastSavedAt }) => {
  // Tick every 30s so the relative-time string in the 'saved' state
  // stays current without depending on parent re-renders.
  const [, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== "saved") return undefined;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [status]);

  if (status === "saving") {
    return (
      <span
        data-testid="autosave-indicator"
        data-autosave-status="saving"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span
        data-testid="autosave-indicator"
        data-autosave-status="pending"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400"
      >
        <Cloud className="h-3.5 w-3.5" />
        Unsaved changes…
      </span>
    );
  }
  if (status === "saved") {
    const rel = lastSavedAt ? formatRelative(lastSavedAt) : "";
    const abs = lastSavedAt ? formatTime(lastSavedAt) : "";
    return (
      <span
        data-testid="autosave-indicator"
        data-autosave-status="saved"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600"
        title={abs ? `Last saved at ${abs}` : ""}
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        Saved{rel ? ` · ${rel}` : ""}
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        data-testid="autosave-indicator"
        data-autosave-status="error"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Couldn't autosave — your edits are still here, please save manually.
      </span>
    );
  }
  return null;
};

function formatTime(d) {
  try {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "";
  }
}

// "just now" / "1m ago" / "23m ago" / "1h ago" / "3h ago"
// Buckets are deliberately coarse to avoid an animation-y feel.
function formatRelative(d) {
  try {
    const diffMs = Date.now() - d.getTime();
    const sec = Math.max(0, Math.round(diffMs / 1000));
    if (sec < 30) return "just now";
    const min = Math.round(sec / 60);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    return `${day}d ago`;
  } catch (_) {
    return "";
  }
}

export default AutosaveIndicator;
